import { CSSProperties } from 'react';
import { StorageData } from '~Components/Storage';
import { i18nProps } from '~Types';

export const DEFAULT_FONT_SIZE = 16;

export type Styling = {
  type: string;
  fontSize?: number;
  fontWeight?: CSSProperties['fontWeight'];
  textAlign?: CSSProperties['textAlign'];
  styleType?: 'outline' | 'filled';
  fontStyle?: CSSProperties['fontStyle'];
  marginTop?: number;
  marginBottom?: number;
  width?: string | number;
  textTransform?: CSSProperties['textTransform'];

  // Only for some elements
  showPrice?: boolean;
  prefix?: string;
  useAbbreviation?: boolean;

  // Only for ITEM_FROM_OTHER_STATION
  showDetails?: boolean;

  // Group
  divider?: boolean;
  spaceBetweenGroups?: number;
  spaceBetweenItems?: number;
};

export const DEFAULT_TICKET_FONT_SIZE = 16;

// Returns default styling object, modified based on storage settings, will be overwritten if custom template is used
export const getDefaultTicketStyling = ({
  storage,
  i18n,
  isTextPrinting,
}: {
  storage: StorageData;
  i18n: i18nProps;
  isTextPrinting?: boolean;
}) => {
  let obj: Record<string, Omit<Styling, 'type'>> = {};

  if (isTextPrinting) {
    obj = {
      TITLE: { fontSize: 35, marginBottom: 4 },
      PRINT_ID: { fontSize: 10 },
      FLOORPLAN: { fontSize: 14 },
      DATE: { fontSize: 14 },
      STAFF_NAME: { fontSize: 14 },
      COVERAGE: { fontSize: 14 },
      DEVICE: { fontSize: 14 },
      DEVICE_NAME: { fontSize: 14 },
      SERVICE_TYPE: { fontSize: 30 },
      ESTIMATED_TIME: { fontSize: 14 },
      DESIRED_TIME: { fontSize: 14 },
      DELIVERY_LOCATION: { fontSize: 14 },
      ORDER_COMMENT: { fontSize: 14 },
      TICKET_MESSAGE: { fontSize: 24 },
      GROUP_TITLE: { fontSize: 20, useAbbreviation: true },
      ITEM_TITLE: { fontSize: 20, showPrice: false, textTransform: 'uppercase' },
      VARIATION: { fontSize: 14, showPrice: false, prefix: '• ' },
      REMOVED_VARIATION: { fontSize: 14, prefix: i18n('Bong.Remove') + ': ' },
      ITEM_COMMENT: { fontSize: 14 },
      STATION_TITLE: { fontSize: 14, marginTop: 10 },
      STATION_NAME: { fontSize: 20, marginBottom: 10 },
      ITEM_FROM_OTHER_STATION: { fontSize: 14, fontStyle: 'italic', showDetails: false },
      GROUP: { spaceBetweenItems: 6, divider: true, spaceBetweenGroups: 30 },
    };
  } else {
    obj = {
      TITLE: { fontSize: 30, fontWeight: 'bold', marginBottom: 4 },
      PRINT_ID: { fontSize: 10 },
      FLOORPLAN: {},
      DATE: {},
      STAFF_NAME: {},
      COVERAGE: {},
      DEVICE: {},
      DEVICE_NAME: {},
      SERVICE_TYPE: { fontSize: 24, fontWeight: 'bold' },
      ESTIMATED_TIME: {},
      DESIRED_TIME: { styleType: 'outline', width: '100%', textAlign: 'center', marginTop: 16 },
      DELIVERY_LOCATION: {},
      ORDER_COMMENT: { styleType: 'outline', width: '100%', textAlign: 'center', marginTop: 16 },
      TICKET_MESSAGE: { fontSize: 30 },
      GROUP_TITLE: {
        fontSize: 18,
        fontWeight: 'bold',
        useAbbreviation: true,
      },
      ITEM_TITLE: { fontSize: 24, showPrice: false, textTransform: 'uppercase' },
      VARIATION: { fontSize: 18, showPrice: false, prefix: '• ' },
      REMOVED_VARIATION: { fontSize: 18, styleType: 'filled', prefix: i18n('Bong.Remove') + ': ' },
      ITEM_COMMENT: { fontSize: 18, styleType: 'outline' },
      STATION_TITLE: { fontSize: 14, marginTop: 10 },
      STATION_NAME: { fontSize: 30, fontWeight: 'bold', marginBottom: 10 },
      ITEM_FROM_OTHER_STATION: { fontSize: 14, fontStyle: 'italic', showDetails: false },
      GROUP: { spaceBetweenItems: 6, divider: true, spaceBetweenGroups: 30 },
    };
  }

  if (storage.BongDisableCapitalLetters) {
    obj.ITEM_TITLE.textTransform = 'none';
  }

  if (storage.bongShowPrices) {
    obj.ITEM_TITLE.showPrice = true;
    obj.VARIATION.showPrice = true;
  }

  return obj;
};

// The ordering of these elements makes the structure of the ticket, If some element is missing it will not be shown.
// Will be overwritten if custom template is used
export const getDefaultTicketStructure = ({
  storage,
  isTextPrinting,
}: {
  storage: StorageData;
  isTextPrinting?: boolean;
}) => {
  let arr: { type: string; height?: number }[] = [];

  if (isTextPrinting) {
    arr = [
      { type: 'TITLE' },
      { type: 'FLOORPLAN' },
      { type: 'DATE' },
      { type: 'STAFF_NAME' },
      { type: 'COVERAGE' },
      { type: 'DEVICE' },
      { type: 'SERVICE_TYPE' },
      { type: 'DESIRED_TIME' },
      { type: 'DELIVERY_LOCATION' },
      { type: 'STATION' },
      { type: 'DIVIDER' },
      { type: 'MAIN_CONTENT' }, // << MAIN_CONTENT placeholder (this is where main content will go)
      { type: 'DIVIDER' },
      { type: 'TICKET_MESSAGE' },
      { type: 'ORDER_COMMENT' },
      { type: 'SPACE', height: 50 },
    ];
  } else {
    arr = [
      { type: 'TITLE' },
      { type: 'FLOORPLAN' },
      { type: 'DATE' },
      { type: 'STAFF_NAME' },
      { type: 'COVERAGE' },
      { type: 'DEVICE' },
      { type: 'SERVICE_TYPE' },
      { type: 'DESIRED_TIME' },
      { type: 'DELIVERY_LOCATION' },
      { type: 'STATION' },
      { type: 'SPACE', height: 10 },
      { type: 'ORDER_COMMENT' },
      { type: 'SPACE', height: 24 },
      { type: 'MAIN_CONTENT' }, // << MAIN_CONTENT placeholder (this is where main content will go)
      { type: 'SPACE', height: 24 },
      { type: 'TICKET_MESSAGE' },
      { type: 'SPACE', height: 50 },
    ];
  }

  // If bong print ID is enabled, add PRINT_ID at the top after TITLE and also space before and after it, otherwise just add space before the TITLE
  if (storage.BongShowPrintId) {
    if (isTextPrinting) {
      arr.splice(0, 0, { type: 'SPACE', height: 50 });
      arr.splice(1, 0, { type: 'PRINT_ID' });
      arr.splice(2, 0, { type: 'SPACE', height: 100 });
    } else {
      arr.splice(0, 0, { type: 'SPACE', height: 10 });
      arr.splice(1, 0, { type: 'PRINT_ID' });
      arr.splice(2, 0, { type: 'SPACE', height: 128 });
    }
  } else {
    if (isTextPrinting) {
      arr.splice(0, 0, { type: 'SPACE', height: 150 });
    } else {
      arr.splice(0, 0, { type: 'SPACE', height: 138 });
    }
  }

  if (storage.BongHideAllExceptDesiredTime) {
    arr = arr.filter((item) => !['DATE'].includes(item.type));
  }

  if (!storage.bongShowFloorplan) {
    arr = arr.filter((item) => item.type !== 'FLOORPLAN');
  }

  if (storage.hideServiceType) {
    arr = arr.filter((item) => item.type !== 'SERVICE_TYPE');
  }

  if (!storage.bongPrintByStation) {
    arr = arr.filter((item) => item.type !== 'STATION');
  }

  return arr;
};
